/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { SceneType } from "../commons";
import { UnityConfig } from "react-unity-webgl/distribution/types/unity-config";
import ConfigureItem from "./configureitem";
import { Environments, Utility } from "./index";
import { EventHandler } from "@rfice/eventhandler";
import { Properties } from "@/frameworks/libraries";
import { Properties as LibProperties } from "@rsupport/libraries";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const LOGTAG = "#Configure#";

export type SpaceUIOptionType = {
	minimizeSpace?: boolean;
};

export type ChatUIOptionType = {
	prioritySpaceChat?: boolean;
	showFullSizeChat?: boolean;
};

export type RecentConferenceType = {
	conferenceId: string;
	sceneType: SceneType;
	sceneLevel: string;
	accessCode: string;
	path?: string;
	version?: string;
};

export type OpeningScheduleConference = {
	scheduleId: string;
	conference: SwaggerMeetingAPIModels.Conference;
};

export type MiniChatCoordinateType = { [id: string]: { x: number; y: number } };
export type FoldingBPositionType = { [id: string]: { height: UnitString } };
export type VoiceCallStartType = { [roomID: string]: string };
export type RecordingSeqNumType = Record<string, { seqNum: string }>;

abstract class ConfigureInterface {
	protected readonly _partnerMode = new ConfigureItem<boolean | null>("pm", null);
	public abstract get partnerMode(): boolean | null;
	public abstract get isPartners(): boolean;

	protected readonly _authorization_key = new ConfigureItem<string | null>("ak", null);
	protected readonly _refresh_token = new ConfigureItem<string | null>("rt", null);
	protected readonly _user_id = new ConfigureItem<string>("id", "", { useCrypto: true });
	public abstract get is_authenticated(): boolean;
	public abstract clear_authenticated(): void;
	public abstract get authorizationKey(): string | null;
	public abstract set authorizationKey(value: string | null);
	public abstract get refreshToken(): string | null;
	public abstract set refreshToken(value: string | null);
	public abstract get userID(): string;
	public abstract set userID(value: string | null);

	protected readonly _useRMAudioOption = new ConfigureItem("useRMAudioOption", "0");
	public abstract get useRMAudioOption(): boolean;

	protected readonly _useLogLevel = new ConfigureItem("useLogLevel", "5");
	public abstract get useLogLevel(): number;

	protected readonly _useCompatMode = new ConfigureItem("useCompatMode", true);
	public abstract get useCompatMode(): boolean;

	protected readonly _useLanguageDebug = new ConfigureItem("useLanguageDebug", false);
	public abstract get useLanguageDebug(): boolean;

	protected readonly _useLanguage = new ConfigureItem<LanguageType>("useLanguage", "ko");
	public abstract get useLanguage(): LanguageType;
	public abstract set useLanguage(value: LanguageType);

	protected readonly _usePixelRatio = new ConfigureItem<number>("usePixelRatio", -1);
	public abstract get usePixelRatio(): number;

	protected readonly _useUnityLogger = new ConfigureItem<boolean>("useUnityLogger", true);
	public abstract get useUnityLogger(): boolean;
	public abstract set useUnityLogger(value: boolean);

	protected readonly _useWebGLOptions = new ConfigureItem<UnityConfig["webglContextAttributes"]>("useWebGLOptions", {
		// alpha: false,
		// antialias: false,
		// depth: false,
		// failIfMajorPerformanceCaveat: true,
		// powerPreference: 0,
		// premultipliedAlpha: true,
		// preserveDrawingBuffer: true,
		// stencil: true,
		// desynchronized: true,
		// xrCompatible: true
	});
	public abstract get useWebGLOptions(): UnityConfig["webglContextAttributes"] | null | undefined;

	protected readonly _recentSearchList = new ConfigureItem<Record<string, unknown[] | null>>(
		"rsl",
		{},
		{ useCrypto: true, useUniqueID: true }
	);
	public abstract get recentSearchList(): Record<string, unknown[] | null>;
	public abstract set recentSearchList(value: Record<string, unknown[] | null>);

	protected readonly _spaceUIOption = new ConfigureItem<SpaceUIOptionType>(
		"suo",
		{
			minimizeSpace: false
		},
		{
			useCrypto: true,
			useUniqueID: true
		}
	);
	public abstract get spaceUIOption(): SpaceUIOptionType;
	public abstract set spaceUIOption(value: SpaceUIOptionType);

	protected readonly _chatUIOption = new ConfigureItem<ChatUIOptionType>(
		"cuo",
		{},
		{
			useCrypto: true,
			useUniqueID: true
		}
	);
	public abstract get chatUIOption(): ChatUIOptionType;
	public abstract set chatUIOption(value: ChatUIOptionType);
	protected readonly _miniChatCoordinate = new ConfigureItem<MiniChatCoordinateType>(
		"mcc",
		{},
		{
			useCrypto: true,
			useUniqueID: true
		}
	);
	public abstract get miniChatCoordinate(): MiniChatCoordinateType;
	public abstract set miniChatCoordinate(value: MiniChatCoordinateType);

	protected readonly _disconnectedByOtherDevice = new ConfigureItem<boolean>("dbod", false, {
		storageType: "session"
	});
	public abstract get disconnectedByOtherDevice(): boolean | null;
	public abstract set disconnectedByOtherDevice(value: boolean | null);

	protected readonly _chatLanguage = new ConfigureItem<"ko" | "ja" | "en" | null>("clg", "ko");
	public abstract get chatLanguage(): "en" | "ko" | "ja" | null;
	public abstract set chatLanguage(value: string);

	// protected readonly _browserTabID = new ConfigureItem<string | null>("btid", null);
	// public abstract get browserTabID(): string | null;
	// public abstract set browserTabID(value: string | null);

	// protected readonly _priorityBrowserTabID = new ConfigureItem<string | null>("sbtid", null);
	// public abstract get priorityBrowserTabID(): string | null;
	// public abstract set priorityBrowserTabID(value: string | null);

	protected readonly _foldingBPosition = new ConfigureItem<FoldingBPositionType>("fbp", {});
	public abstract get foldingBPosition(): FoldingBPositionType;
	public abstract set foldingBPosition(value: FoldingBPositionType);

	protected readonly _voiceCallStarter = new ConfigureItem<VoiceCallStartType>("vcs", {});
	public abstract get voiceCallStarter(): VoiceCallStartType;
	public abstract set voiceCallStarter(value: VoiceCallStartType);

	protected readonly _deviceSettings = new ConfigureItem<Device.Settings | null>("dss", null, { useCrypto: false });
	public abstract get deviceSettings(): Device.Settings | null;
	public abstract set deviceSettings(value: Device.Settings | null);

	protected readonly _mydeskMode = new ConfigureItem<boolean>("mdm", false);
	public abstract get mydeskMode(): boolean;
	public abstract set mydeskMode(value: boolean);

	protected readonly _isNotifyOnceForNotification = new ConfigureItem<boolean | null>("nofn", false);
	public abstract get isNotifyOnceForNotification(): boolean | null;
	public abstract set isNotifyOnceForNotification(value: boolean | null);

	protected readonly _recordingSeqNumList = new ConfigureItem<RecordingSeqNumType>(
		"rsn",
		{},
		{ useCrypto: false, storageType: "session" }
	);
	public abstract get recordingSeqNumList(): RecordingSeqNumType;
	public abstract set recordingSeqNumList(value: RecordingSeqNumType);

	protected readonly _userActiveStatus = new ConfigureItem<
		WebServices.UserActiveStatus.Update.Data["activeStatus"] | null
	>("uas", null);
	public abstract get userActiveStatus(): WebServices.UserActiveStatus.Update.Data["activeStatus"] | null;
	public abstract set userActiveStatus(value: WebServices.UserActiveStatus.Update.Data["activeStatus"] | null);

	protected readonly _currentConference = new ConfigureItem<RecentConferenceType | null>("cc", null, {
		useCrypto: true,
		useUniqueID: true,
		storageType: "session"
	});
	public abstract get currentConference(): RecentConferenceType | null;
	public abstract set currentConference(value: RecentConferenceType | null);

	protected readonly _shouldScrollToToday = new ConfigureItem<"T" | "F">("sST", "F");
	public abstract get shouldScrollToToday(): "T" | "F";
	public abstract set shouldScrollToToday(value: "T" | "F");

	protected readonly _openingScheduleConference = new ConfigureItem<OpeningScheduleConference | null>("osc", null, {
		useCrypto: true,
		useUniqueID: true
	});
	public abstract get openingScheduleConference(): OpeningScheduleConference | null;
	public abstract set openingScheduleConference(value: OpeningScheduleConference | null);

	protected readonly _api_domain = new ConfigureItem<string | null>("ad", null);
	public abstract get api_domain(): string | null;
	protected readonly _web_domain = new ConfigureItem<string | null>("wd", null);
	public abstract get web_domain(): string | null;
	protected readonly _partner_domain = new ConfigureItem<string | null>("pd", null);
	public abstract get partner_domain(): string | null;

	protected readonly _use_device_id = new ConfigureItem<boolean>("udi", false);
	public abstract get use_device_id(): boolean;

	/**
	 * Indicates whether the experimental feature is enabled or not.
	 *
	 * This method is abstract and should be implemented by subclasses
	 * to specify the activation state of the experimental feature.
	 *
	 * @return {boolean} true if the experimental feature is enabled; false otherwise.
	 */
	public abstract get use_experimental_feature(): boolean;
	protected readonly _use_experimental_feature = new ConfigureItem<boolean>("useExperimentalFeature", true);
}

class ConfigureSingleton extends ConfigureInterface {
	constructor() {
		super();
	}

	/**
	 * Retrieves the current state of the partner mode.
	 *
	 * @return {boolean} The current state of the partner mode.
	 */
	public get partnerMode(): boolean | null {
		return this._partnerMode.read;
	}

	/**
	 * Determines if the current window's origin matches the partner domain or a specific localhost port.
	 *
	 * @return {boolean} True if the current origin includes the partner domain or starts with "http://localhost:3020", otherwise false.
	 */
	public get isPartners(): boolean {
		if (this.partnerMode === null) {
			const partnerURL = Utility.URLNormalize(this.partner_domain);
			return (
				window.location.origin.includes(partnerURL) ||
				window.location.origin.startsWith("http://localhost:3000/partner")
			);
		} else {
			return this.partnerMode;
		}
	}

	public get is_authenticated() {
		if (this.authorizationKey) {
			return this.authorizationKey.length > 0;
		}
		return false;
	}

	public clear_authenticated() {
		this.authorizationKey = null;
		this.refreshToken = null;
		this.userID = null;
		Properties.finger_print = null;

		EventHandler.emit("authorization_changed", "cleaned");
	}

	public get authorizationKey() {
		return this._authorization_key.read;
	}

	public set authorizationKey(value: string | null) {
		this._authorization_key.write = value;

		EventHandler.emit("authorization_changed", "authorization_key", value);
	}

	public get refreshToken() {
		return this._refresh_token.read;
	}

	public set refreshToken(value: string | null) {
		this._refresh_token.write = value;

		EventHandler.emit("authorization_changed", "refresh_token", value);
	}

	public get userID(): string {
		return this._user_id.read;
	}

	public set userID(value: string | null) {
		this._user_id.write = value;
		const old_selected = LibProperties.instance.selected;
		LibProperties.instance.selected = value;
		EventHandler.emit("user_id_changed", old_selected, value);
	}

	public get useRMAudioOption(): boolean {
		const value = parseInt(this._useRMAudioOption.read);
		return value === 1;
	}

	public get useLogLevel() {
		return parseInt(this._useLogLevel.read);
	}

	public get useCompatMode() {
		return this._useCompatMode.read;
	}

	public get useLanguageDebug() {
		return this._useLanguageDebug.read;
	}

	public get useLanguage() {
		return this._useLanguage.read;
	}

	public set useLanguage(value: LanguageType) {
		this._useLanguage.write = value;
	}

	public get usePixelRatio() {
		return this._usePixelRatio.read;
	}

	public get useUnityLogger() {
		return this._useUnityLogger.read;
	}

	public set useUnityLogger(value: boolean) {
		this._useUnityLogger.write = value;
	}

	public get useWebGLOptions(): UnityConfig["webglContextAttributes"] {
		return this._useWebGLOptions.read;
	}

	public get recentSearchList() {
		return this._recentSearchList.read;
	}

	public set recentSearchList(value: Record<string, unknown[] | null>) {
		this._recentSearchList.write = value;
	}

	public get spaceUIOption() {
		if (this._spaceUIOption.read) {
			return this._spaceUIOption.read;
		} else {
			return {};
		}
	}

	public set spaceUIOption(value: SpaceUIOptionType) {
		this._spaceUIOption.write = value;
	}

	public get chatUIOption() {
		if (this._chatUIOption.read) {
			return this._chatUIOption.read;
		} else {
			return {};
		}
	}

	public set chatUIOption(value: ChatUIOptionType) {
		this._chatUIOption.write = value;
	}

	public get miniChatCoordinate() {
		return this._miniChatCoordinate.read;
	}

	public set miniChatCoordinate(value: MiniChatCoordinateType) {
		this._miniChatCoordinate.write = value;
	}

	public get disconnectedByOtherDevice() {
		return this._disconnectedByOtherDevice.read;
	}

	public set disconnectedByOtherDevice(value: boolean | null) {
		this._disconnectedByOtherDevice.write = value;
	}

	public get chatLanguage() {
		return this._chatLanguage.read;
	}

	public set chatLanguage(value) {
		this._chatLanguage.write = value;
	}

	// public get browserTabID() {
	// 	return this._browserTabID.read;
	// }
	//
	// public set browserTabID(value: string | null) {
	// 	this._browserTabID.write = value;
	// }
	//
	// public get priorityBrowserTabID() {
	// 	return this._priorityBrowserTabID.read;
	// }
	//
	// public set priorityBrowserTabID(value: string | null) {
	// 	this._priorityBrowserTabID.write = value;
	// }

	public get foldingBPosition() {
		return this._foldingBPosition.read;
	}

	public set foldingBPosition(value: FoldingBPositionType) {
		this._foldingBPosition.write = value;
	}

	public get voiceCallStarter() {
		return this._voiceCallStarter.read;
	}

	public set voiceCallStarter(value: VoiceCallStartType) {
		this._voiceCallStarter.write = Object.keys(value).length <= 0 ? null : { ...this.voiceCallStarter, ...value };
	}

	public get deviceSettings() {
		return this._deviceSettings.read;
	}

	public set deviceSettings(value: Device.Settings | null) {
		this._deviceSettings.write = value;
	}

	public get mydeskMode() {
		return this._mydeskMode.read;
	}

	public set mydeskMode(value: boolean) {
		this._mydeskMode.write = value;
	}

	public get isNotifyOnceForNotification() {
		return this._isNotifyOnceForNotification.read;
	}

	public set isNotifyOnceForNotification(value: boolean | null) {
		this._isNotifyOnceForNotification.write = value;
	}

	public get recordingSeqNumList() {
		if (this._recordingSeqNumList.read) {
			return this._recordingSeqNumList.read;
		}
		return {};
	}

	public set recordingSeqNumList(value: RecordingSeqNumType) {
		this._recordingSeqNumList.write = value;
	}

	public get userActiveStatus(): WebServices.UserActiveStatus.Update.Data["activeStatus"] | null {
		return this._userActiveStatus.read;
	}
	public set userActiveStatus(value: WebServices.UserActiveStatus.Update.Data["activeStatus"] | null) {
		this._userActiveStatus.write = value;
	}

	public get currentConference() {
		return this._currentConference.read;
	}
	public set currentConference(value: RecentConferenceType | null) {
		this._currentConference.write = value;
	}

	public get shouldScrollToToday(): "T" | "F" {
		return this._shouldScrollToToday.read || "F";
	}
	public set shouldScrollToToday(value: "T" | "F") {
		this._shouldScrollToToday.write = value;
	}

	public get openingScheduleConference() {
		return this._openingScheduleConference.read;
	}
	public set openingScheduleConference(value: OpeningScheduleConference | null) {
		this._openingScheduleConference.write = value;
	}

	public get api_domain(): string | null {
		if (!this._api_domain.read) {
			return Environments.env<string | null>("API_DOMAIN");
		}
		return this._api_domain.read;
	}

	public get web_domain(): string | null {
		if (!this._web_domain.read) {
			return Environments.env<string | null>("WEB_DOMAIN");
		}
		return this._web_domain.read;
	}

	public get partner_domain(): string | null {
		if (!this._partner_domain.read) {
			return Environments.env<string | null>("PARTNER_DOMAIN");
		}
		return this._partner_domain.read;
	}

	public get use_device_id(): boolean {
		return this._use_device_id.read;
	}

	public get use_experimental_feature(): boolean {
		return this._use_experimental_feature.read;
	}
}

export const Configure = new ConfigureSingleton();
